import {IAccountant} from "api/types";
import Tooltip from "components/Tooltip";
import {Plus} from "lucide-react";
import {FC, useState} from "react";
import Review from "./ProfileCard/Review";
import {extractUrl} from "../utils";

interface Props {
  user: IAccountant;
  editable: boolean;
  updating: boolean;
  onChangeReview: (name: string, url: string) => void;
  onDeleteReview: (name: string) => void;
}

const ReviewsRow: FC<Props> = ({user, editable, updating, onChangeReview, onDeleteReview}) => {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");

  const onAdd = () => {
    if (!newName.trim() || !newUrl.trim()) return;
    onChangeReview(newName.trim(), newUrl.trim());
    setNewName("");
    setNewUrl("");
    setAdding(false);
  };

  const reviews = Object.entries(user.reviews || {});

  return (
    <div className="rounded-2xl px-6 py-4 mb-5 border border-brand-border bg-white">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-brand-muted mr-1">Reviews</span>

        {reviews.map(([name, val]) => (
          <Review
            key={name}
            name={name}
            url={extractUrl(val)}
            editable={editable}
            onChange={onChangeReview}
            onDelete={editable ? () => onDeleteReview(name) : undefined}
            disabled={updating}
          />
        ))}

        {reviews.length === 0 && !editable && (
          <span className="text-xs text-brand-muted italic">No reviews added yet.</span>
        )}

        {editable && !adding && (
          <Tooltip
            title="Add New Review Link"
            subtitle="Link to Google, Facebook, Yelp, etc."
            position="top"
            withoutPointer
          >
            <button
              onClick={() => setAdding(true)}
              disabled={updating}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-brand-border text-xs text-brand-muted hover:text-brand-navy hover:border-brand-navy transition-colors disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Review
            </button>
          </Tooltip>
        )}
      </div>

      {editable && adding && (
        <div className="mt-3 pt-3 border-t border-brand-border flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={newName}
            autoFocus
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Platform name"
            className="text-xs rounded-lg px-2.5 py-1.5 outline-none border w-36"
            style={{backgroundColor: "#F7F5F2", borderColor: "#E8E4DF", color: "#282D4D"}}
          />
          <input
            type="text"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="Review URL"
            className="text-xs rounded-lg px-2.5 py-1.5 outline-none border w-48"
            style={{backgroundColor: "#F7F5F2", borderColor: "#E8E4DF", color: "#282D4D"}}
            onKeyDown={(e) => {
              if (e.key === "Enter") onAdd();
              if (e.key === "Escape") setAdding(false);
            }}
          />
          <button
            onClick={onAdd}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white hover:opacity-90 transition-opacity"
            style={{backgroundColor: "#3D52AD"}}
          >
            Add
          </button>
          <button
            onClick={() => {
              setAdding(false);
              setNewName("");
              setNewUrl("");
            }}
            className="text-xs text-brand-muted hover:text-brand-navy transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewsRow;
